import React, { FunctionComponent, useEffect, useState } from "react";

import MaterialTable from "@material-table/core";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Snackbar
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch } from "react-redux";
import useStyles from "../styles";

import * as TenantApi from "../../../api/tenantApi";
import { Alert } from "@material-ui/lab";
import { DistributorSelect } from "../../../components/selects/DistributorSelect";
import { TenantDistributorManager } from "../../../models/Tenant";
import { isOk } from "../../../result";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { AdminSelect } from "../../../components/selects/AdminSelect";
import { useI18n } from "../../../hooks/useI18n";

interface ManageTenantDistributorManagersProps {
  tenantId: string;
}

export const ManageTenantDistributorManagers: FunctionComponent<
  ManageTenantDistributorManagersProps
> = ({ tenantId }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useI18n();

  const [state, setState] = useState<any>({
    reloadList: false,
    distributorManagerList: [] as TenantDistributorManager[],
    showAddDialog: false,
    showDeleteDialog: false,
    editingDistributorManager: {} as TenantDistributorManager,
    showSuccess: false
  });
  const disabled = () => {
    if (
      !state.editingDistributorManager?.personId ||
      !state.editingDistributorManager?.tenantDistributorId
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (state.reloadList) {
      TenantApi.listTenantDistributorManagers(tenantId).then(result => {
        if (isOk(result)) {
          setState({
            ...state,
            distributorManagerList: result.value,
            reloadList: false
          });
        }
      });
    }
  }, [state]);

  useEffect(() => {
    TenantApi.listTenantDistributorManagers(tenantId).then(result => {
      if (isOk(result)) {
        setState({
          ...state,
          distributorManagerList: result.value
        });
      }
    });
  }, []);

  return (
    <>
      <MaterialTable
        title=""
        isLoading={!state.distributorManagerList}
        columns={[
          { title: t("common.formFields.id"), field: "id", hidden: true },
          { title: t("common.distributor"), field: "distributorName" },
          {
            title: t("common.tableHeaders.externalManagers"),
            field: "personName"
          }
        ]}
        data={
          state.distributorManagerList &&
          state.distributorManagerList.length > 0
            ? state.distributorManagerList
            : []
        }
        options={{
          pageSize: 10,
          actionsColumnIndex: -1
        }}
        actions={[
          {
            icon: "add_circle",
            iconProps: {
              style: { color: "rgb(31, 132, 52)", fontSize: "2rem" }
            },
            tooltip: t("common.distributorManagerManagement.addNewManager"),
            isFreeAction: true,
            onClick: () => {
              setState({
                ...state,
                addFailed: false,
                editingDistributorManager: {
                  personId: "",
                  tenantDistributorId: ""
                },
                showAddDialog: true
              });
            }
          },
          {
            icon: "delete",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.deleteManager"),
            onClick: (event, row: any) => {
              const editingDistributorManager =
                state.distributorManagerList.find((x: any) => x.id == row?.id);
              setState({
                ...state,
                deleteFailed: false,
                editingDistributorManager: editingDistributorManager,
                showDeleteDialog: true
              });
            }
          }
        ]}
        localization={{
          header: {
            actions: ""
          }
        }}
      />

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showAddDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() => setState({ ...state, showAddDialog: false })}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <DistributorSelect
            tenantId={tenantId}
            className={classes.field}
            value={
              state?.editingDistributorManager?.tenantDistributorId
                ? state?.editingDistributorManager?.tenantDistributorId
                : ""
            }
            onSelected={distributor =>
              setState({
                ...state,
                editingDistributorManager: {
                  ...state.editingDistributorManager,
                  tenantDistributorId: distributor?.id
                }
              })
            }
          />
          <AdminSelect
            label={t("common.formFields.person") + " *"}
            className={classes.field}
            value={state.editingDistributorManager?.personId}
            onSelected={administrator =>
              setState({
                ...state,
                editingDistributorManager: {
                  ...state.editingDistributorManager,
                  personId: administrator?.id
                }
              })
            }
          />
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 30 }}
          >
            <Button
              style={{ width: 350 }}
              variant="contained"
              color="primary"
              disabled={disabled()}
              onClick={async () => {
                const result = await TenantApi.addTenantDistributorManager(
                  state?.editingDistributorManager
                );
                if (!isOk(result)) {
                  setState({ ...state, addFailed: true });
                } else {
                  setState({
                    ...state,
                    showAddDialog: false,
                    showSuccess: true,
                    reloadList: true
                  });
                }
              }}
            >
              {t("common.distributorManagerManagement.saveDistributorManager")}
            </Button>
            {state.addFailed && (
              <Alert severity="error">
                {t(
                  "common.distributorManagerManagement.errorAddingDistributorManager"
                )}
              </Alert>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        visible={state.showDeleteDialog}
        titleText={t(
          "common.distributorManagerManagement.removeDistributorManager"
        )}
        bodyText={t(
          "common.distributorManagerManagement.removeDistributorManagerConfirmation"
        )}
        confirmText={t(
          "common.distributorManagerManagement.removeDistributorManager"
        )}
        onConfirm={async () => {
          const result = await TenantApi.deleteTenantDistributorManager(
            state?.editingDistributorManager?.id
          );
          if (!isOk(result)) {
            setState({ ...state, deleteFailed: true });
          } else {
            setState({
              ...state,
              showDeleteDialog: false,
              showSuccess: true,
              reloadList: true
            });
          }
          return true;
        }}
        onCancel={() => {
          setState({
            ...state,
            showDeleteDialog: false
          });
        }}
      >
        {state.deleteFailed && (
          <Alert severity="error">
            {t(
              "common.distributorManagerManagement.errorDeletingDistributorManager"
            )}
          </Alert>
        )}
      </ConfirmDialog>

      <Snackbar
        open={state.showSuccess}
        autoHideDuration={2000}
        onClose={() => {
          setState({ ...state, showSuccess: false });
        }}
      >
        <Alert
          onClose={() => setState({ ...state, showSuccess: false })}
          severity="success"
        >
          {t("common.distributorManagerManagement.operationComplete")}
        </Alert>
      </Snackbar>
    </>
  );
};
